import React, {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
} from "react";
import * as d3 from "d3";
import { OrgChart } from "d3-org-chart";
import {
	ArrowClockwise,
	ArrowCounterClockwise,
	ArrowsOut,
	FloppyDisk,
	PencilSimple,
	X,
} from "@phosphor-icons/react";
import jsPDF from "jspdf";
import { Document, Packer, Paragraph, ImageRun } from "docx";
import { saveAs } from "file-saver";
import organizationalStructureService from "services/OrganizationalStructureService";

const OrgChartComponent = forwardRef(({ data, onNodeClick }, ref) => {
	const initialNodes = JSON.parse(JSON.stringify(data)); //cópia profunda para garantir que initialNodes e data sejam independentes.
	const chartRef = useRef(null);
	let dragNode;
	let dropNode;
	let dragEnabled = false;
	let dragStartX;
	let dragStartY;
	let isDragStarting = false;

	let undoActions = [];
	let redoActions = [];

	const generateContent = (d) => {
		return `
			<div class="node-container" 
				style="width: 220px; height: 60px; padding-left: 1px; padding-right: 1px;">
				
				<div class="content-container" 
					style="
						background-color: #ffffff;
						border: ${
							d.data._highlighted ||
							d.data._upToTheRootHighlighted
								? "5px solid #E27396"
								: "1px solid #2a3042"
						};
						border-radius: 5px;
						width: 218px; /* 220px - 2px de padding */
						height: 60px;
						position: relative;
						overflow: hidden;
						box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
						transition: box-shadow 0.3s ease;
						box-sizing: border-box;
						display: flex;
						flex-direction: column;
					">
					
					<!-- Nome -->
					<div style="
						background-color:${d.data.color_name || "#2a3042"};
						color: #fff;
						text-align: center;
						font-family: ${d.data.font_family || "inherit"};
						line-height: 20px;
						border-top-left-radius: 5px;
						border-top-right-radius: 5px;
						font-size: 14px;
						font-weight: bold;
						width: 100%;
						height: 20px;
						box-sizing: border-box;
						overflow: hidden;
						white-space: nowrap;
						text-overflow: ellipsis;
					">
						${d.data.name}
					</div>
					
					<!-- Título -->
					<div style="
						color: #2a3042;
						background-color: ${d.data.color_responsible || "#ffffff"};
						text-align: center;
						font-family: ${d.data.font_family || "inherit"};
						line-height: 40px;
						border-bottom-left-radius: 5px;
						border-bottom-right-radius: 5px;
						font-size: 12px;
						width: 100%;
						height: 40px;
						box-sizing: border-box;
						overflow: hidden;
						white-space: nowrap;
						text-overflow: ellipsis;
					">
						${d.data.title}
					</div>
					
				</div>
			</div>
		`;
	};

	const onDragStart = (element, dragEvent, node) => {
		dragNode = node;
		const width = dragEvent.subject.width;
		const half = width / 2;
		const x = dragEvent.x - half;
		dragStartX = x;
		dragStartY = parseFloat(dragEvent.y);
		isDragStarting = true;

		d3.select(element).classed("dragging", true);
	};

	const onDrag = (element, dragEvent) => {
		if (!dragNode) {
			return;
		}

		const state = chartRef.current.getChartState();
		const g = d3.select(element);

		if (isDragStarting) {
			isDragStarting = false;
			document
				.querySelector(".chart-container")
				.classList.add("dragging-active");

			g.raise();

			const descendants = dragEvent.subject.descendants();
			const linksToRemove = [...(descendants || []), dragEvent.subject];
			const nodesToRemove = descendants.filter(
				(x) => x.data.id !== dragEvent.subject.id,
			);

			state["linksWrapper"]
				.selectAll("path.link")
				.data(linksToRemove, (d) => state.nodeId(d))
				.remove();

			if (nodesToRemove) {
				state["nodesWrapper"]
					.selectAll("g.node")
					.data(nodesToRemove, (d) => state.nodeId(d))
					.remove();
			}
		}

		dropNode = null;
		const cP = {
			width: dragEvent.subject.width,
			height: dragEvent.subject.height,
			left: dragEvent.x,
			right: dragEvent.x + dragEvent.subject.width,
			top: dragEvent.y,
			bottom: dragEvent.y + dragEvent.subject.height,
			midX: dragEvent.x + dragEvent.subject.width / 2,
			midY: dragEvent.y + dragEvent.subject.height / 2,
		};

		const allNodes = d3.selectAll("g.node:not(.dragging)");
		allNodes.select("rect").attr("fill", "none");

		allNodes
			.filter(function (d2, i) {
				const cPInner = {
					left: d2.x,
					right: d2.x + d2.width,
					top: d2.y,
					bottom: d2.y + d2.height,
				};

				if (
					cP.midX > cPInner.left &&
					cP.midX < cPInner.right &&
					cP.midY > cPInner.top &&
					cP.midY < cPInner.bottom &&
					this.classList.contains("droppable")
				) {
					dropNode = d2;
					return d2;
				}
			})
			.select("rect")
			.attr("fill", "#e4e1e1");

		dragStartX += parseFloat(dragEvent.dx);
		dragStartY += parseFloat(dragEvent.dy);
		g.attr("transform", "translate(" + dragStartX + "," + dragStartY + ")");
	};

	const onDragEnd = (element, dragEvent) => {
		document
			.querySelector(".chart-container")
			.classList.remove("dragging-active");

		if (!dragNode) {
			return;
		}

		d3.select(element).classed("dragging", false);

		if (!dropNode) {
			chartRef.current.render();
			return;
		}

		if (dragEvent.subject.parent.id === dropNode.id) {
			chartRef.current.render();
			return;
		}

		d3.select(element).remove();

		const node = data.find((x) => x.id === dragEvent.subject.id);
		const oldParentId = node.parentId;
		node.parentId = dropNode.id;

		redoActions = [];
		undoActions.push({
			id: dragEvent.subject.id,
			parentId: oldParentId,
		});

		dropNode = null;
		dragNode = null;
		chartRef.current.render();
		updateDragActions();
	};

	const enableDrag = () => {
		dragEnabled = true;
		document
			.querySelector(".chart-container")
			.classList.add("drag-enabled");
		document.getElementById("enableDragButton").classList.add("hide");
		document.getElementById("dragActions").classList.remove("hide");
	};

	const disableDrag = () => {
		dragEnabled = false;
		document
			.querySelector(".chart-container")
			.classList.remove("drag-enabled");
		document.getElementById("enableDragButton").classList.remove("hide");
		document.getElementById("dragActions").classList.add("hide");
		undoActions = [];
		redoActions = [];
		updateDragActions();
		updateNodes();
	};

	const cancelDrag = () => {
		if (undoActions.length === 0) {
			disableDrag();
			return;
		}

		const data = chartRef.current.getChartState().data;
		undoActions.reverse().forEach((action) => {
			const node = data.find((x) => x.id === action.id);
			if (node) {
				node.parentId = action.parentId; // Reverte o parentId para o valor anterior
			}
		});

		disableDrag();
		chartRef.current.render(); // Re-renderiza o gráfico com as alterações revertidas
	};

	const undo = () => {
		const action = undoActions.pop();
		if (action) {
			const node = data.find((x) => x.id === action.id);
			const currentParentId = node.parentId;
			const previousParentId = action.parentId;
			action.parentId = currentParentId;
			node.parentId = previousParentId;

			redoActions.push(action);
			chartRef.current.render();
			updateDragActions();
		}
	};

	const redo = () => {
		const action = redoActions.pop();
		if (action) {
			const node = data.find((x) => x.id === action.id);
			const currentParentId = node.parentId;
			const previousParentId = action.parentId;
			action.parentId = currentParentId;
			node.parentId = previousParentId;
			undoActions.push(action);
			chartRef.current.render();
			updateDragActions();
		}
	};

	const updateDragActions = () => {
		const undoButton = document.getElementById("undoButton");
		const redoButton = document.getElementById("redoButton");

		if (undoActions.length > 0) {
			undoButton.disabled = false;
		} else {
			undoButton.disabled = true;
		}

		if (redoActions.length > 0) {
			redoButton.disabled = false;
		} else {
			redoButton.disabled = true;
		}
	};

	const handleFitScreen = () => {
		if (chartRef.current) {
			chartRef.current.fit();
		}
	};

	const downloadPdf = () => {
		if (chartRef.current) {
			chartRef.current.exportImg({
				full: true,
				save: false,
				onLoad: (base64) => {
					const pdf = new jsPDF();
					const img = new Image();
					img.src = base64;
					img.onload = function () {
						pdf.addImage(
							img,
							"JPEG",
							5,
							5,
							595 / 3,
							((img.height / img.width) * 595) / 3,
						);
						pdf.save("estrutura-organizacional.pdf");
					};
				},
			});
		}
	};

	const downloadWord = () => {
		if (chartRef.current) {
			chartRef.current.exportImg({
				full: true,
				save: false,
				onLoad: (base64) => {
					const img = new Image();
					img.src = base64;
					img.onload = function () {
						const pageWidth = 595;
						const imgWidth = pageWidth;
						const imgHeight = (img.height / img.width) * imgWidth;

						const doc = new Document({
							sections: [
								{
									properties: {},
									children: [
										new Paragraph({
											children: [
												new ImageRun({
													data: base64,
													transformation: {
														width: imgWidth,
														height: imgHeight,
													},
												}),
											],
										}),
									],
								},
							],
						});

						Packer.toBlob(doc).then((blob) => {
							saveAs(blob, "estrutura-organizacional.docx");
						});
					};
				},
			});
		}
	};

	// Expõe as funções downloadPdf e downloadWord para o componente pai
	useImperativeHandle(ref, () => ({
		downloadPdf,
		downloadWord,
	}));

	const updateNodes = async () => {
		const updatedNodes = getUpdatedNodes(
			initialNodes,
			chartRef.current.getChartState().data,
		);

		try {
			for (const node of updatedNodes) {
				const { id, parentId } = node;
				await organizationalStructureService.put(id, {
					parent_id: parentId,
				});
			}
		} catch (error) {
			console.error("Erro ao atualizar nós:", error);
		}
	};

	const getUpdatedNodes = (initialNodes, newNodes) => {
		return newNodes.filter((newNode) => {
			const oldNode = initialNodes.find((n) => n.id === newNode.id);
			return oldNode && oldNode.parentId !== newNode.parentId;
		});
	};

	useEffect(() => {
		if (!data || data.length === 0) return;

		const sortData = (input) => {
			const sorted = [...input];
			const parentIds = [
				...new Set(sorted.map((d) => d.parentId).filter(Boolean)),
			];

			parentIds.forEach((parentId) => {
				const children = sorted.filter((d) => d.parentId === parentId);
				const reordered = [
					...children.filter((d) => d.is_support_area),
					...children.filter((d) => !d.is_support_area),
				];
				for (const child of children) {
					const index = sorted.findIndex((d) => d.id === child.id);
					if (index !== -1) sorted.splice(index, 1);
				}
				sorted.push(...reordered);
			});

			return sorted;
		};

		const sortedData = sortData(data);

		const initializeChart = () => {
			chartRef.current = new OrgChart()
				.nodeHeight((d) => 60)
				.nodeWidth((d) => 220)
				.compact(false)
				.childrenMargin((d) => 50)
				.compactMarginBetween((d) => 35)
				.compactMarginPair((d) => 30)
				.neighbourMargin((a, b) => 20)
				.nodeContent(function (d, i, arr, state) {
					return generateContent(d);
				})
				.nodeEnter(function (node) {
					d3.select(this).call(
						d3
							.drag()
							.filter(function (x, node) {
								return (
									dragEnabled &&
									this.classList.contains("draggable")
								);
							})
							.on("start", function (d, node) {
								onDragStart(this, d, node);
							})
							.on("drag", function (dragEvent, node) {
								onDrag(this, dragEvent);
							})
							.on("end", function (d) {
								onDragEnd(this, d);
							}),
					);
				})
				.onNodeClick(function (d) {
					if (onNodeClick) {
						onNodeClick(d.data);
					}
				})
				.nodeUpdate(function (d) {
					// Verifica se o nó é o nó raiz (não tem parentId)
					const isRootNode =
						d.data.parentId === null ||
						d.data.parentId === undefined;

					// Define se o nó é droppable (aqui nesse caso todos)
					d3.select(this).classed("droppable", true);

					// Define se o nó é draggable (apenas nós não raiz)
					d3.select(this).classed("draggable", !isRootNode);
				})
				.linkUpdate(function (d, i, arr) {
					// Estilo das linhas de conexão
					d3.select(this).attr("stroke", "#2a3042");
				})
				.linkUpdate(function (d, i, arr) {
					const targetData = d.data;

					d3.select(this)
						.attr(
							"stroke",
							targetData.is_support_area ? "#999" : "#2a3042",
						)
						.attr("stroke-width", 2)
						.attr(
							"stroke-dasharray",
							targetData.is_support_area ? "4,2" : "0",
						);
				})
				.container(".chart-container")
				.data(sortedData)
				.render();

			chartRef.current.expandAll();
			chartRef.current.fit();

			// Adicionar event listeners aos botões
			document
				.getElementById("enableDragButton")
				.addEventListener("click", enableDrag);
			document
				.getElementById("finishDrag")
				.addEventListener("click", disableDrag);
			document
				.getElementById("undoButton")
				.addEventListener("click", undo);
			document
				.getElementById("redoButton")
				.addEventListener("click", redo);
			document
				.getElementById("cancelDrag")
				.addEventListener("click", cancelDrag);
			document
				.getElementById("fitScreen")
				.addEventListener("click", handleFitScreen);
		};

		// Inicializar o gráfico
		initializeChart();

		// Limpeza ao desmontar o componente
		return () => {
			// Remover event listeners, se necessário
			document
				.getElementById("enableDragButton")
				?.removeEventListener("click", enableDrag);
			document
				.getElementById("finishDrag")
				?.removeEventListener("click", disableDrag);
			document
				.getElementById("undoButton")
				?.removeEventListener("click", undo);
			document
				.getElementById("redoButton")
				?.removeEventListener("click", redo);
			document
				.getElementById("cancelDrag")
				?.removeEventListener("click", cancelDrag);
			document
				.getElementById("fitScreen")
				?.removeEventListener("click", handleFitScreen);
		};
	}, [data]);

	return (
		<div>
			<style>
				{`
          .hide {
            display: none;
          }

          .drag-enabled:not(.dragging-active) .node.draggable {
            stroke: grey;
            stroke-width: 3px;
            stroke-dasharray: 2px;
          }

          .drag-enabled.dragging-active .droppable {
            stroke: green;
            stroke-width: 3px;
            stroke-dasharray: 5px;
          }

          .node.dragging {
            stroke-dasharray: 0 !important;
            stroke-width: 0 !important;
          }

          .node.dragging .content-container {
            background-color: #ffffff;
          }

        #dragActions {
            display: none; 
            flex-direction: row; 
            gap: 10px; 
        }

        #dragActions:not(.hide) {
            display: flex;
        }

        .btn-action-button {
            text-transform: lowercase;
            font-size: 11px !important;
            border-radius: 5px !important;
            color: white !important;
            padding: 4px 5px !important;
            background-color: #2a3042 !important;
        }

        .action-buttons {
            position: absolute;
            top: 20px;
            right: 20px;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 10px;
            z-index: 1000;
        }
        `}
			</style>

			<div className="action-buttons">
				<button id="fitScreen" className="btn-action-button">
					<ArrowsOut size={16} />
					Ajustar tela
				</button>

				<button id="enableDragButton" className="btn-action-button">
					<PencilSimple size={16} /> Editar estrutura
				</button>
				<div id="dragActions" className="hide">
					<button id="finishDrag" className="btn-action-button">
						<FloppyDisk size={16} /> Salvar
					</button>
					<button
						id="undoButton"
						className="btn-action-button"
						disabled
					>
						<ArrowCounterClockwise size={16} /> Desfazer
					</button>
					<button
						id="redoButton"
						className="btn-action-button"
						disabled
					>
						<ArrowClockwise size={16} /> Refazer
					</button>
					<button id="cancelDrag" className="btn-action-button">
						<X size={16} /> Cancelar
					</button>
				</div>
			</div>

			<div className="chart-container"></div>
		</div>
	);
});

export default OrgChartComponent;
